/**
 * Id-keyed registry of the foreground editor's action handlers.
 *
 * The top-bar "..." menu (EditorOptionsMenu) lives in the layout header — a
 * sibling of the page content, not an ancestor — so it can't hold a ref to the
 * editor instance. Instead each editor registers its action handlers under its
 * document id, and the menu invokes them by the id it already knows (the route
 * id for the top-bar, an explicit prop for the peek).
 *
 * This replaces the old `editor:action-*` / `editor:state-update` eventBus
 * broadcast. Unlike a fire-and-forget event, lookups here are:
 *   - current-value: the menu reads the live handler set at click time, with
 *     no replay/timing race on mount;
 *   - id-scoped: a peek and the main editor (both mounted) register under
 *     different ids and never cross wires.
 */
export interface EditorActions {
  /** Open the share dialog for this document. */
  share: () => void;
  /** Open the version-history view. */
  history: () => void;
  /** Toggle the activity-log panel. */
  activity: () => void;
  /** Copy the page contents (HTML + plain text) to the clipboard. */
  copyContents: () => void | Promise<void>;
}

const registry = new Map<string, EditorActions>();

/**
 * Register an editor's action handlers under `id`. Returns a disposer that
 * removes the entry — but only if it still points at THIS registration, so a
 * remount that registers the new instance before the old one's cleanup runs
 * doesn't wipe the newer entry.
 */
export function registerEditorActions(id: string, actions: EditorActions): () => void {
  registry.set(id, actions);
  return () => {
    if (registry.get(id) === actions) registry.delete(id);
  };
}

/** Look up the action handlers for `id`, if an editor is mounted for it. */
export function getEditorActions(id: string | null | undefined): EditorActions | undefined {
  return id ? registry.get(id) : undefined;
}
